import { useState } from "react";
import {
  useCmsTournaments,
  useCreateCmsTournament,
  useUpdateCmsTournament,
} from "@/hooks/useCmsTournaments";
import { tournamentPortalAdminUrl, tournamentPortalDomain } from "@/types/tournament";
import type { CmsTournamentResponse } from "@/types/tournament";
import { Trophy, Plus, ExternalLink, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface TournamentsTabProps {
  organizationId: string;
}

/** Client-side mirror of the API slug rule (^[a-z0-9-]{3,63}$) for instant feedback. */
const SLUG_RE = /^[a-z0-9-]{3,63}$/;

/** Suggests a slug from a tournament name: lowercase, hyphens, trimmed to 63. */
function suggestSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63);
}

/** Local yyyy-MM-dd from a date input → ISO-8601 UTC (platform DateTime convention). */
function toUtcIso(localDate: string): string | null {
  return localDate ? new Date(localDate).toISOString() : null;
}

interface FormState {
  name: string;
  nameAr: string;
  slug: string;
  slugTouched: boolean;
  startDate: string;
  endDate: string;
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  name: "",
  nameAr: "",
  slug: "",
  slugTouched: false,
  startDate: "",
  endDate: "",
  isActive: true,
};

export function TournamentsTab({ organizationId }: TournamentsTabProps) {
  const { data: tournaments = [], isLoading, error } = useCmsTournaments(organizationId);
  const createMutation = useCreateCmsTournament(organizationId);
  const updateMutation = useUpdateCmsTournament(organizationId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CmsTournamentResponse | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const slugValid = SLUG_RE.test(form.slug);
  const canSubmit = form.name.trim().length > 0 && slugValid && !createMutation.isPending && !updateMutation.isPending;

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (t: CmsTournamentResponse) => {
    setEditing(t);
    setForm({
      // Generated swagger types mark scalar fields optional — coalesce for the form.
      name: t.name ?? "",
      nameAr: t.nameAr ?? "",
      slug: t.slug ?? "",
      slugTouched: true,
      startDate: t.startDate ? t.startDate.slice(0, 10) : "",
      endDate: t.endDate ? t.endDate.slice(0, 10) : "",
      isActive: t.isActive ?? true,
    });
    setDialogOpen(true);
  };

  const handleNameChange = (name: string) => {
    setForm((f) => ({
      ...f,
      name,
      // Auto-suggest the slug from the name until the user edits the slug manually.
      slug: f.slugTouched ? f.slug : suggestSlug(name),
    }));
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    const shared = {
      name: form.name.trim(),
      nameAr: form.nameAr.trim() || null,
      slug: form.slug,
      startDate: toUtcIso(form.startDate),
      endDate: toUtcIso(form.endDate),
    };
    if (editing?.id) {
      updateMutation.mutate(
        { tournamentId: editing.id, data: { ...shared, isActive: form.isActive } },
        { onSuccess: () => setDialogOpen(false) }
      );
    } else {
      createMutation.mutate(shared, { onSuccess: () => setDialogOpen(false) });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-12 bg-muted animate-pulse rounded" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-destructive">
          Failed to load tournaments: {String(error)}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Tournaments are bootstrapped here with their public address; all further
          configuration happens in the Tournament Portal.
        </p>
        <Button onClick={openCreate} data-testid="create-tournament-button">
          <Plus className="h-4 w-4 mr-2" />
          Create Tournament
        </Button>
      </div>

      {tournaments.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center space-y-2">
            <Trophy className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="font-medium">No tournaments yet</p>
            <p className="text-sm text-muted-foreground">
              Create the organization&apos;s first tournament to unlock its Tournament
              Portal at {"{slug}"}.{tournamentPortalDomain()}.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tournaments.map((t) => (
                <TableRow key={t.id} data-testid={`tournament-row-${t.id}`}>
                  <TableCell>
                    <div className="font-medium">{t.name}</div>
                    {t.nameAr && <div className="text-xs text-muted-foreground" dir="rtl">{t.nameAr}</div>}
                  </TableCell>
                  <TableCell>
                    {t.slug ? (
                      <a
                        href={tournamentPortalAdminUrl(t.slug)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline font-mono text-sm"
                      >
                        {t.slug}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <span className="text-muted-foreground text-sm">no slug</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{t.status}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {t.startDate ? t.startDate.slice(0, 10) : "—"}
                    {t.endDate ? ` → ${t.endDate.slice(0, 10)}` : ""}
                  </TableCell>
                  <TableCell>
                    <Badge variant={t.isActive ? "default" : "secondary"}>
                      {t.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(t)} aria-label={`Edit ${t.name}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Tournament" : "Create Tournament"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Update the tournament's basic details."
                : "Only the basics are needed here — the organization's admins finish the configuration in the Tournament Portal."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="tournament-name">Name</Label>
              <Input
                id="tournament-name"
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Presidential Guard Cup 2026"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="tournament-name-ar">Arabic name (optional)</Label>
              <Input
                id="tournament-name-ar"
                dir="rtl"
                value={form.nameAr}
                onChange={(e) => setForm((f) => ({ ...f, nameAr: e.target.value }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="tournament-slug">Public address (slug)</Label>
              <Input
                id="tournament-slug"
                value={form.slug}
                onChange={(e) =>
                  setForm((f) => ({ ...f, slug: e.target.value.toLowerCase(), slugTouched: true }))
                }
                placeholder="pg-2026"
                className="font-mono"
              />
              <p className={`text-xs ${form.slug && !slugValid ? "text-destructive" : "text-muted-foreground"}`}>
                {form.slug && !slugValid
                  ? "3-63 characters: lowercase letters, numbers and hyphens only."
                  : form.slug
                    ? `Site: ${form.slug}.${tournamentPortalDomain()}`
                    : "Required — this becomes the tournament's web address."}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="tournament-start">Start date (optional)</Label>
                <Input
                  id="tournament-start"
                  type="date"
                  value={form.startDate}
                  onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tournament-end">End date (optional)</Label>
                <Input
                  id="tournament-end"
                  type="date"
                  value={form.endDate}
                  onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                />
              </div>
            </div>

            {editing && (
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <Label htmlFor="tournament-active">Active</Label>
                  <p className="text-xs text-muted-foreground">
                    Inactive tournaments stop resolving on their public address.
                  </p>
                </div>
                <Switch
                  id="tournament-active"
                  checked={form.isActive}
                  onCheckedChange={(checked) => setForm((f) => ({ ...f, isActive: checked }))}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit} data-testid="submit-tournament">
              {editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
